/* eslint-disable max-lines */
import React, { useCallback, useMemo, useState } from 'react';

import { useTranslation } from '@modernloop/i18n';
import { EmployeeId } from '@modernloop/shared/ids';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Divider,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { DynamicAudience, TemplateType } from 'src/generated/mloop-graphql';

import AddAttachments from 'src/components/AddAttachments';
import Editor from 'src/components/Editor';
import { useReactQuillRef } from 'src/components/QuillEditor';

import SmartEmployeeSelect, { SmartEmployeeSelectResult } from 'src/entities/Select/SmartEmployeeSelect';
import TemplateSelect from 'src/entities/Template/TemplateSelect';

import { useInitAttachmentsForEmailTemplate } from 'src/hooks/useAttachments';
import { LoggerEvent, useLogEvent } from 'src/hooks/useLogEvent';

import { FileUploadFlow } from 'src/slices/files';

export type EmailContentTemplate = {
  id: string;
  subject?: string | null;
  body?: string | null;
  type: TemplateType;
  name?: string | null;
};

type Props = {
  title?: string;
  applicationId?: string;
  alerts?: React.ReactNode;
  taskId?: string;
  jobId?: string;
  taskCreatorId?: string;
  subscriberEmployeeIds?: string[];
  interviewersOnScheduleIds?: string[];
  assigneeEmployeeId?: string;
  ccEmployeeIds?: string[];
  defaultCcEmployeeIds?: string[];
  bccEmployeeIds?: string[];
  defaultBccEmployeeIds?: string[];
  toFreeformEmails?: string[];
  ccFreeformEmails?: string[];
  defaultCcFreeformEmails?: string[];
  bccFreeformEmails?: string[];
  defaultBccFreeformEmails?: string[];
  ccDynamicAudiences?: DynamicAudience[];
  bccDynamicAudiences?: DynamicAudience[];
  filterCcDynamicAudiences?: DynamicAudience[];
  filterBccDynamicAudiences?: DynamicAudience[];
  /**
   * selectedToFreeformEmail email is used to determine selected email addres when `useEmployeePickerForTo` is falsy
   */
  selectedToFreeformEmail?: string;
  loading?: boolean;
  subject: string;
  bodyHtml: string;
  fileUploadFlow: FileUploadFlow;
  sendError?: string;

  templateType: TemplateType;
  selectedTemplateId?: string;

  onToChanged?: (freeformEmails: string[]) => void;
  onCcChanged?: (result: SmartEmployeeSelectResult) => void;
  onBccChanged?: (result: SmartEmployeeSelectResult) => void;
  onChangeSubject: (value: string) => void;
  onChangeBodyHtml: (value: string) => void;
  onChangeTemplate?: (value: EmailContentTemplate) => void;
};

const EmailContent = ({
  title,
  alerts,
  ccEmployeeIds = [],
  defaultCcEmployeeIds = [],
  bccEmployeeIds = [],
  defaultBccEmployeeIds,
  taskId,
  jobId,
  taskCreatorId,
  subscriberEmployeeIds = [],
  assigneeEmployeeId,
  interviewersOnScheduleIds = [],
  applicationId,
  ccDynamicAudiences = [],
  toFreeformEmails = [],
  ccFreeformEmails = [],
  defaultCcFreeformEmails = [],
  bccFreeformEmails = [],
  bccDynamicAudiences,
  defaultBccFreeformEmails = [],
  selectedToFreeformEmail,
  loading,
  subject,
  bodyHtml,
  fileUploadFlow,
  sendError,
  templateType,
  selectedTemplateId,
  filterCcDynamicAudiences = [],
  filterBccDynamicAudiences = [],
  onToChanged,
  onCcChanged,
  onBccChanged,
  onChangeSubject,
  onChangeBodyHtml,
  onChangeTemplate,
}: Props) => {
  const { t } = useTranslation();
  const { logEvent } = useLogEvent();

  const [useCc, setUseCc] = useState(
    Boolean(
      defaultCcEmployeeIds?.length ||
      ccEmployeeIds?.length ||
      defaultCcFreeformEmails?.length ||
      ccFreeformEmails?.length ||
      ccDynamicAudiences?.length
    )
  );
  const [useBcc, setUseBcc] = useState(
    Boolean(
      defaultBccEmployeeIds?.length ||
      bccEmployeeIds?.length ||
      defaultBccFreeformEmails?.length ||
      bccFreeformEmails?.length ||
      bccDynamicAudiences?.length
    )
  );

  const [internalSelectedTemplateId, setInternalSelectedTemplateId] = useState<string>();

  const getSelectedTemplateId = () => {
    return selectedTemplateId || internalSelectedTemplateId || '';
  };

  useInitAttachmentsForEmailTemplate(getSelectedTemplateId(), fileUploadFlow);

  const quillRef = useReactQuillRef();

  const toEmailOptions: { id: string; value: string; selected: boolean }[] = useMemo(() => {
    if (!toFreeformEmails) {
      return [];
    }
    return toFreeformEmails.map((toEmail) => ({
      id: toEmail,
      value: toEmail,
      selected: toEmail === selectedToFreeformEmail,
    }));
  }, [selectedToFreeformEmail, toFreeformEmails]);

  const handleToggleUseCc = useCallback(() => setUseCc(!useCc), [useCc]);
  const handleToggleUseBcc = useCallback(() => setUseBcc(!useBcc), [useBcc]);

  const handleTo = useCallback(
    (freeformEmails: string[]) => {
      if (onToChanged) {
        onToChanged(freeformEmails);
      }
    },
    [onToChanged]
  );

  const handleSmartCc = useCallback(
    (result: SmartEmployeeSelectResult) => {
      logEvent(LoggerEvent.CLIENT_SMART_OPTIONS_APPLIED, { type: 'CC - Email content' });
      if (onCcChanged) {
        onCcChanged(result);
      }
    },
    [logEvent, onCcChanged]
  );

  const handleSmartBcc = useCallback(
    (result: SmartEmployeeSelectResult) => {
      logEvent(LoggerEvent.CLIENT_SMART_OPTIONS_APPLIED, { type: 'BCC - Email content' });

      if (onBccChanged) {
        onBccChanged(result);
      }
    },
    [logEvent, onBccChanged]
  );

  const handleTemplateSelectEmailTemplateChange = useCallback(
    (template: EmailContentTemplate) => {
      if (!template || !onChangeTemplate) {
        return;
      }
      setInternalSelectedTemplateId(template.id);
      onChangeTemplate(template);
    },
    [onChangeTemplate]
  );

  const selectedToFreeformEmailId = toEmailOptions.find((option) => option.id === selectedToFreeformEmail)?.id;

  const jsx = (
    <>
      <Stack direction="column" spacing={2}>
        {sendError && (
          <Alert severity="error">
            <AlertTitle>{sendError}</AlertTitle>
          </Alert>
        )}

        <Stack>
          <Typography variant="subtitle2">{t('hiring-dashboard-nav:nav.emailContent.to')}</Typography>
          <Select
            // Adding this to trigger a re-render when initially loaded
            key={`to-email-select-${selectedToFreeformEmailId}`}
            fullWidth
            disabled={!toEmailOptions.length}
            value={selectedToFreeformEmailId}
            onChange={(option) => handleTo([(option.target.value as string) || ''])}
          >
            {toEmailOptions.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.value}
              </MenuItem>
            ))}
          </Select>
        </Stack>

        {!useCc && !useBcc && (
          <Stack direction="row" alignItems="center">
            <Button variant="text" color="primary" onClick={handleToggleUseCc}>
              {t('hiring-dashboard-nav:nav.emailContent.cc')}
            </Button>
            <Button variant="text" color="primary" onClick={handleToggleUseBcc}>
              {t('hiring-dashboard-nav:nav.emailContent.bcc')}
            </Button>
          </Stack>
        )}

        {(useCc || useBcc) && (
          <Stack direction="column" spacing={2}>
            {!useCc && (
              <Button sx={{ maxWidth: '50px' }} variant="text" color="primary" onClick={handleToggleUseCc}>
                {t('hiring-dashboard-nav:nav.emailContent.cc')}
              </Button>
            )}
            {useCc && (
              <SmartEmployeeSelect
                label={t('hiring-dashboard-nav:nav.emailContent.cc')}
                placeholder={t('hiring-dashboard-nav:nav.emailContent.addNamesOrEmails')}
                filterDynamicAudiences={filterCcDynamicAudiences}
                context={{
                  task: taskId ?? {
                    TASK_ASSIGNEE: assigneeEmployeeId ?? '',
                    TASK_CREATOR: taskCreatorId ?? '',
                    TASK_SUBSCRIBERS: subscriberEmployeeIds,
                    DEBRIEF_ATTENDEES: [],
                  },
                  job: jobId,
                  schedule: interviewersOnScheduleIds?.length
                    ? {
                        INTERVIEWERS_ON_SCHEDULE: interviewersOnScheduleIds,
                      }
                    : undefined,
                  application: applicationId,
                  enableOptionsWithMissingData: false,
                }}
                onChange={handleSmartCc}
                selectedEmployeeIds={EmployeeId.parseArray(ccEmployeeIds)}
                selectedDynamicAudiences={ccDynamicAudiences}
                selectedFreeformEmails={ccFreeformEmails}
              />
            )}

            {!useBcc && (
              <Button sx={{ maxWidth: '50px' }} variant="text" color="primary" onClick={handleToggleUseBcc}>
                {t('hiring-dashboard-nav:nav.emailContent.bcc')}
              </Button>
            )}
            {useBcc && (
              <SmartEmployeeSelect
                label={t('hiring-dashboard-nav:nav.emailContent.bcc')}
                placeholder={t('hiring-dashboard-nav:nav.emailContent.addNamesOrEmails')}
                filterDynamicAudiences={filterBccDynamicAudiences}
                context={{
                  task: taskId ?? {
                    TASK_ASSIGNEE: assigneeEmployeeId ?? '',
                    TASK_CREATOR: taskCreatorId ?? '',
                    TASK_SUBSCRIBERS: subscriberEmployeeIds,
                    DEBRIEF_ATTENDEES: [],
                  },
                  schedule: interviewersOnScheduleIds?.length
                    ? {
                        INTERVIEWERS_ON_SCHEDULE: interviewersOnScheduleIds,
                      }
                    : undefined,
                  job: jobId,
                  application: applicationId,
                  enableOptionsWithMissingData: false,
                }}
                onChange={handleSmartBcc}
                selectedEmployeeIds={EmployeeId.parseArray(bccEmployeeIds)}
                selectedDynamicAudiences={bccDynamicAudiences}
                selectedFreeformEmails={bccFreeformEmails}
              />
            )}
          </Stack>
        )}
      </Stack>

      <Divider sx={{ margin: '8px 0px 12px 0px' }} />

      <Stack direction="column" spacing={2}>
        <TemplateSelect
          minimalUX
          useSelectButton
          onTemplateSelect={handleTemplateSelectEmailTemplateChange}
          selectedTemplateId={getSelectedTemplateId()}
          type={templateType}
        />
        {loading && <LinearProgress sx={{ width: '100%' }} />}

        <TextField
          fullWidth
          value={subject}
          onChange={(event) => {
            if (!event?.target) {
              return;
            }
            onChangeSubject(event.target.value);
          }}
          error={!subject && !loading}
          label={
            !subject
              ? t('hiring-dashboard-nav:nav.emailContent.subjectRequired')
              : t('hiring-dashboard-nav:nav.emailContent.subject')
          }
          disabled={loading}
        />
        <Editor
          readOnly={loading}
          sx={loading ? { opacity: 0.5 } : {}}
          height={480}
          value={bodyHtml}
          quillRef={quillRef}
          onChange={(newValue: string) => {
            onChangeBodyHtml(newValue);
          }}
        />
        {alerts}
        <AddAttachments flow={fileUploadFlow} />
      </Stack>
    </>
  );

  return (
    <Stack spacing={1}>
      <Typography variant="h4">{title}</Typography>
      <Divider />
      <Box width="100%">{jsx}</Box>
    </Stack>
  );
};

export default EmailContent;
